import {
	CombineMethod,
	GradeName,
	GradeThresholds,
} from './riskSetupDraft.types';

// The sample pipe used by the live worked example on Risk Setup sits at a
// fixed fraction of each axis maximum (LoF 8 / CoF 6 on a 0–10 scale), so it
// stays meaningful on 0–5 and 0–100 models alike.
export const EXAMPLE_LOF_FRACTION = 0.8;
export const EXAMPLE_COF_FRACTION = 0.6;
const DEFAULT_AXIS_MAX = 10;

const usableMax = (max: number | undefined): number =>
	max !== undefined && Number.isFinite(max) && max > 0
		? max
		: DEFAULT_AXIS_MAX;

export interface AxisMaxima {
	maxLoFValue?: number;
	maxCoFValue?: number;
}

export const exampleSample = ({
	maxLoFValue,
	maxCoFValue,
}: AxisMaxima): { lof: number; cof: number } => ({
	lof: EXAMPLE_LOF_FRACTION * usableMax(maxLoFValue),
	cof: EXAMPLE_COF_FRACTION * usableMax(maxCoFValue),
});

// Mirrors saas_risk_management RiskService.CalculateRISK:
// evalWeightedScoreMulti (0/NaN → 1) and evalWeightedScoreAdd (0/NaN → 0).
const multiplyOperand = (value: number): number => value || 1;
const addOperand = (value: number): number => value || 0;

// Mirrors RiskGrades: >= checked from Extreme down, else Negligible.
export const gradeForScore = (
	score: number,
	thresholds: GradeThresholds,
): GradeName => {
	if (score >= thresholds.extreme) return 'extreme';
	if (score >= thresholds.high) return 'high';
	if (score >= thresholds.medium) return 'medium';
	if (score >= thresholds.low) return 'low';
	return 'negligible';
};

const thresholdsComplete = ({
	low,
	medium,
	high,
	extreme,
}: GradeThresholds): boolean =>
	[low, medium, high, extreme].every(value => Number.isFinite(value));

export interface WorkedExampleResult {
	weightedLof: number;
	weightedCof: number;
	operator: '×' | '+';
	score: number;
	grade: GradeName | null;
}

export const computeWorkedExample = ({
	lofWeighting,
	cofWeighting,
	riskMethod,
	thresholds,
	useNormalize,
	maxLoFValue,
	maxCoFValue,
}: AxisMaxima & {
	lofWeighting: number;
	cofWeighting: number;
	riskMethod: CombineMethod | undefined;
	thresholds: GradeThresholds;
	useNormalize: boolean;
}): WorkedExampleResult => {
	const sample = exampleSample({ maxLoFValue, maxCoFValue });
	const weightedLof = (sample.lof * lofWeighting) / 100;
	const weightedCof = (sample.cof * cofWeighting) / 100;
	const multiplied = riskMethod === 'multiplied';
	const score = multiplied
		? multiplyOperand(weightedLof) * multiplyOperand(weightedCof)
		: addOperand(weightedLof) + addOperand(weightedCof);
	// With normalization the BE rescales against the network's highest score,
	// so a pre-run grade can't be known. A threshold cleared mid-edit (NaN)
	// would give a misleading grade, so show none until all four are set.
	const grade =
		useNormalize || !thresholdsComplete(thresholds)
			? null
			: gradeForScore(score, thresholds);
	return {
		weightedLof,
		weightedCof,
		operator: multiplied ? '×' : '+',
		score,
		grade,
	};
};

export const formatScore = (value: number): string =>
	Number.isNaN(value) ? '' : value.toFixed(1);

/** Sample-pipe value for display: integer if whole, else 1 dp. */
export const formatSampleValue = (value: number): string =>
	Number.isInteger(value) ? String(value) : value.toFixed(1);
